import { Button, capitalize, IconButton } from '@mui/material';
import { MdAdd, MdClose } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { Sheet, Slot } from '../../../../components/sheet';
import { CustomSlot } from '../../../../components/sheet/slots';
import { getDefaultCycleLengthForBlockType } from './block-form-schema';

interface BlockLayoutInputProps {
  value: number[];
  onChange: (value: number[]) => void;
  cycleLabel: string | null;
  sessionLabel: string | null;
  originalLayout?: number[];
  disabled?: boolean;
  disableAddCycleButton?: boolean;
  isBowlingBlock: boolean;
}

export const BlockLayoutInput = ({
  value,
  onChange,
  disabled,
  cycleLabel,
  sessionLabel,
  disableAddCycleButton = false,
  isBowlingBlock = false,
}: BlockLayoutInputProps) => {
  const { t } = useTranslation();

  const onRemoveCycle = (cycleIdx: number) => {
    onChange(value.filter((_, i) => i !== cycleIdx));
  };

  const addCycle = () => {
    onChange([...value, getDefaultCycleLengthForBlockType(isBowlingBlock)]);
  };

  const handleDayChange = (newValue: string, cycleIdx: number) => {
    const newValueInt = parseInt(newValue) || 0;
    if (isNaN(newValueInt)) {
      return;
    }
    // replace the value at the cycleIdx with the new value
    onChange(value.map((v, i) => (i === cycleIdx ? newValueInt : v)));
  };

  const canDeleteCycle = (cycleIdx: number): boolean => {
    // Can't delete the first cycle (must have at least one)
    return cycleIdx !== 0;
  };

  const getSlotsForBlockLayout = () => {
    const slots: Slot<{ cycleIndex: number; value: number }>[] = [];
    const rowCount = value.length + 1; // +1 for header
    const columnCount = 2;

    // Header row
    slots.push(
      new CustomSlot(
        { x: 0, y: 0 },
        1,
        1,
        false,
        () => (
          <div className="pl-2 flex items-center py-2">
            {cycleLabel ? capitalize(cycleLabel) : t('cycle')}
          </div>
        ),
        {
          className:
            'bg-gray-100 text-slate-600 text-[11px] font-medium leading-tight rounded-tl-md',
        },
      ),
    );

    slots.push(
      new CustomSlot(
        { x: 1, y: 0 },
        1,
        1,
        false,
        () => (
          <div className="pl-2 flex items-center py-2">
            {sessionLabel ? capitalize(sessionLabel) : t('session')}
          </div>
        ),
        {
          className:
            'bg-gray-100 text-slate-600 text-[11px] font-medium leading-tight rounded-tr-md',
        },
      ),
    );

    // Block layout rows
    value.forEach((week, i) => {
      const cycleIndex = i;

      // Cycle number cell
      slots.push(
        new CustomSlot(
          { x: 0, y: i + 1 },
          1,
          1,
          true,
          () => <div className="flex items-center px-2 py-1">{i + 1}</div>,
          { className: 'bg-gray-100 text-sm' },
        ),
      );

      // Session count input cell
      slots.push(
        new CustomSlot(
          { x: 1, y: i + 1 },
          1,
          1,
          // don't allow editing sessions on bowling blocks
          !!disabled || isBowlingBlock,
          (editing) => (
            <div className="flex items-center justify-between w-full h-full px-2">
              {editing ? (
                <input
                  className="h-full min-w-full max-w-0 outline-none"
                  autoFocus
                  defaultValue={String(week || '')}
                  onChange={(e) => {
                    handleDayChange(e.target.value, cycleIndex);
                  }}
                />
              ) : (
                <>
                  <span className="text-sm">{week || ''}</span>
                  {canDeleteCycle(i) && (
                    <IconButton
                      size="small"
                      onClick={() => onRemoveCycle(i)}
                      aria-label={t('deleteCycle', { cycle: i + 1 })}
                      className="text-gray-400 hover:text-gray-600 p-0"
                    >
                      <MdClose aria-hidden="true" size={18} />
                    </IconButton>
                  )}
                </>
              )}
            </div>
          ),
          {
            className: 'bg-white',
            editBehavior: disabled
              ? { type: 'skip' }
              : {
                  type: 'input',
                  onStart: () => {
                    // No state needed - the input will use defaultValue
                  },
                  onCommit: () => {
                    // The onChange handler will handle the commit
                  },
                },
            data: [{ cycleIndex: i, value: week }],
          },
        ),
      );
    });

    return {
      slots,
      rowCount,
      columnCount,
      gridSubRows: 1,
    };
  };

  const { slots, rowCount, columnCount, gridSubRows } =
    getSlotsForBlockLayout();

  return (
    <div>
      <Sheet
        slots={slots}
        rowCount={rowCount}
        columnCount={columnCount}
        gridSubRows={gridSubRows}
        onUndo={() => {}}
        onRedo={() => {}}
        onCopy={() => {}}
        onPaste={() => {}}
        onBulkClear={() => {}}
        getContextMenuContent={() => null}
        disableKeyboardAndMouse={disabled}
      />
      {/** Add cycle button */}
      <Button
        variant="outlined"
        disabled={disabled || disableAddCycleButton}
        className="rounded-b-md mt-2"
        size="small"
        onClick={addCycle}
      >
        <MdAdd aria-hidden="true" size={12} className="mr-1" />
        {t('addCycleLabel', {
          cycleLabel: cycleLabel ? capitalize(cycleLabel) : t('cycle'),
        })}
      </Button>
    </div>
  );
};
